<script lang="ts">
  import AdminShell from "@/components/AdminShell.svelte";
  import LoadingState from "@/components/LoadingState.svelte";
  import Card from "@/components/Card.svelte";
  import { api, ApiError, type AdminAuditEvent, type AdminOverview } from "@/api";
  import { token } from "@/stores/session";
  import { formatDateTime, formatNumber } from "@/utils/format";

  let overview: AdminOverview | null = $state(null);
  let events: AdminAuditEvent[] = $state([]);
  let loading = $state(true);
  let error = $state<string | null>(null);

  async function load() {
    if (!$token) {
      return;
    }
    loading = true;
    try {
      [overview, events] = await Promise.all([
        api.adminOverview($token),
        api.adminFullAudit($token),
      ]);
      error = null;
    } catch (cause) {
      error = cause instanceof ApiError ? cause.message : "Не удалось загрузить обзор";
    } finally {
      loading = false;
    }
  }

  $effect(() => {
    void load();
  });
</script>

<AdminShell section="overview">
  {#if error}
    <p class="form-error">{error}</p>
  {/if}

  {#if loading && !overview}
    <LoadingState message="Собираем состояние системы…" />
  {:else if overview}
    <section class="admin-metrics">
      <div class="metric-card">
        <span>Пользователи</span>
        <strong>{overview.users}</strong>
        <small>всего аккаунтов</small>
      </div>
      <div class="metric-card">
        <span>Серверы</span>
        <strong>{overview.servers}</strong>
        <small
          >{overview.serversByStatus.running} работают · {overview.serversByStatus.failed} с ошибкой</small
        >
      </div>
      <div class="metric-card">
        <span>Ноды</span>
        <strong>{overview.nodes}</strong>
        <small
          >{overview.nodesByStatus.online} в сети · {overview.nodesByStatus.offline} оффлайн</small
        >
      </div>
      <div class="metric-card">
        <span>Кредиты</span>
        <strong>{formatNumber(overview.creditsInCirculation)}</strong>
        <small>{overview.backups} резервных копий</small>
      </div>
    </section>

    <div class="admin-overview-grid">
      <Card class="admin-card">
        <p class="eyebrow">Инфраструктура</p>
        <h2>Состояние</h2>
        <dl class="admin-health">
          <div>
            <dt>Ноды в сети</dt>
            <dd>{overview.nodesByStatus.online} / {overview.nodes}</dd>
          </div>
          <div>
            <dt>Серверы работают</dt>
            <dd>{overview.serversByStatus.running} / {overview.servers}</dd>
          </div>
          <div>
            <dt>В очереди</dt>
            <dd>{overview.serversByStatus.queued}</dd>
          </div>
          <div>
            <dt>Ошибки</dt>
            <dd class="health-danger">{overview.serversByStatus.failed}</dd>
          </div>
        </dl>
      </Card>

      <Card class="admin-card">
        <div class="section-heading">
          <div>
            <p class="eyebrow">Журнал действий</p>
            <h2>Последние изменения</h2>
          </div>
        </div>
        {#if events.length === 0}
          <p class="muted">Событий пока нет.</p>
        {:else}
          <div class="audit-list">
            {#each events.slice(0, 8) as event (event.id)}
              <div>
                <span>
                  <strong>{event.action}</strong>
                  <small>{event.actorUsername ?? "система"} · {event.targetType}</small>
                </span>
                <time>{formatDateTime(event.createdAt)}</time>
              </div>
            {/each}
          </div>
        {/if}
      </Card>
    </div>
  {/if}
</AdminShell>
